<script setup>
/* Tela de DIGITAÇÃO do simulador NOVO — componente PRÓPRIO (decisão do Kairo,
   2026-07-29: o SimuladorModal antigo é SÓ referência visual; nada dele é
   importado ou aberto aqui — ele será deletado quando este simulador ficar
   pronto). Campos, labels e ordem espelham a tela de digitação de referência;
   o backend é o simulador-api:
     POST simulacoes/<id>/bancos/<banco>/digitar {oferta, form}
     GET  simulacoes/<id>/bancos/<banco>/digitar/status   (quando pendente)
   Resposta: {status:'ok'|'pendente'|'erro', codigo, link, mensagem, propostaId} */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import simuladorApi from 'dashboard/api/simuladorApi';
import cltApi from 'dashboard/api/cltSimulador';
import { BANK_STYLE, brl, cpfFmt, familiaDe } from '../bancos.js';

const props = defineProps({
  banco: { type: String, required: true },
  oferta: { type: Object, default: () => ({}) },
  // { cpf, nome, celular, nascimento } — da simulação ativa
  cliente: { type: Object, default: () => ({}) },
  // retorno do Enriquecer (bureau), se o operador já enriqueceu nesta sessão
  enriquecimento: { type: Object, default: () => ({}) },
  simulacaoId: { type: [String, Number], required: true },
});
const emit = defineEmits(['close', 'ok']);

const onlyDigits = s => String(s || '').replace(/\D+/g, '');

/* ---------------------------------------------------------------- estado -- */
// 'form' | 'enviando' | 'pendente' | 'ok'  (erro = banner dentro do 'form')
const estado = ref('form');
const erroMsg = ref('');
const resultado = ref(null); // {codigo, link, mensagem, propostaId}
let pollTimer = null;

const pararPoll = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};
onBeforeUnmount(pararPoll);

/* ------------------------------------------------------------------ form -- */
const f = ref({
  nome: '',
  nascimento: '',
  email: '',
  celular: '',
  sexo: 'M',
  estado_civil: '',
  nome_mae: '',
  ocupacao: '',
  rg_numero: '',
  rg_orgao: '',
  rg_uf: '',
  rg_data: '',
  renda: '',
  matricula: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  // recebimento
  recebTipo: 'pix', // 'pix' | 'conta'
  pix_tipo: 'cpf', // cpf | telefone | email | aleatoria (backend mapeia o enum)
  pix_chave: '',
  banco_codigo: '',
  agencia: '',
  agencia_digito: '',
  conta: '',
  conta_digito: '',
  tipo_conta: 'corrente',
});

const b = computed(() => String(props.banco || '').toLowerCase());
const estilo = computed(() => BANK_STYLE[b.value] || { label: b.value, bg: '#333', fg: '#fff' });

/* v48: as regras de ficha valem para a FAMÍLIA do banco — 'soma_cel' e
   'soma_uy3' (e o legado 'soma') pedem exatamente os mesmos campos, então
   nada aqui compara com as chaves cruas. */
const fam = computed(() => familiaDe(b.value));

/* Quais seções cada banco mostra — mapeado da tela de referência. */
const mostraSexo = computed(() => ['soma', 'facta'].includes(fam.value));
const mostraEstadoCivil = computed(() => ['uy3', 'soma', 'facta'].includes(fam.value));
const mostraNomeMae = computed(() => ['soma', 'facta'].includes(fam.value));
const mostraOcupacao = computed(() => ['uy3', 'soma'].includes(fam.value));
const mostraRgRenda = computed(() => fam.value === 'facta');
const temPix = computed(() => fam.value !== 'c6');
const temConta = computed(() => ['c6', 'soma'].includes(fam.value));
// SOMA aceita PIX OU conta; C6 só conta; os demais só PIX
const mostraToggleReceb = computed(() => fam.value === 'soma');

/* Estado civil com os valores nativos de cada banco (a referência manda). */
const OPCOES_ESTADO_CIVIL = {
  uy3: [
    ['Single', 'Solteiro(a)'],
    ['Married', 'Casado(a)'],
    ['Divorced', 'Divorciado(a)'],
    ['Widowed', 'Viúvo(a)'],
  ],
  soma: [
    ['solteiro', 'Solteiro(a)'],
    ['casado', 'Casado(a)'],
    ['divorciado', 'Divorciado(a)'],
    ['viuvo', 'Viúvo(a)'],
    ['separado', 'Separado(a)'],
    ['uniao', 'União estável'],
  ],
};
const opcoesEstadoCivil = computed(
  () => OPCOES_ESTADO_CIVIL[fam.value] || OPCOES_ESTADO_CIVIL.soma
);

/* -------------------------------------------------------------- prefill -- */
const aplicaEnriquecimento = d => {
  if (!d || !Object.keys(d).length) return;
  const put = (campo, valor) => {
    if (!f.value[campo] && valor) f.value[campo] = String(valor);
  };
  put('nome', d.name);
  if (!f.value.nascimento && d.birthDate) f.value.nascimento = String(d.birthDate).slice(0, 10);
  put('email', d.email);
  put('nome_mae', d.motherName);
  put('rg_numero', d.rgNumber);
  put('rg_orgao', d.rgIssuerOrg);
  put('rg_uf', d.rgIssuerUf);
  put('rg_data', d.rgIssueDate);
  put('cep', d.postalCode);
  put('logradouro', d.street);
  put('numero', d.addressNumber);
  put('bairro', d.neighborhood);
  put('cidade', d.city);
  put('uf', d.state);
  if (!f.value.celular && (d.phoneRegionCode || d.phone))
    f.value.celular = onlyDigits(`${d.phoneRegionCode || ''}${d.phone || ''}`);
};

onMounted(async () => {
  const c = props.cliente || {};
  f.value.nome = c.nome || '';
  f.value.nascimento = c.nascimento || '';
  f.value.celular = onlyDigits(c.celular);
  f.value.estado_civil = opcoesEstadoCivil.value[0]?.[0] || '';
  f.value.pix_chave = onlyDigits(c.cpf);
  if (!temPix.value) f.value.recebTipo = 'conta';
  aplicaEnriquecimento(props.enriquecimento);
  // Sem dados do bureau em mãos? Busca de novo (mesmo endpoint do botão
  // Enriquecer da tela) — best-effort: falha silenciosa, o operador digita.
  if (!f.value.nome_mae || !f.value.cep) {
    try {
      const { data } = await cltApi.get('enriquecer', { cpf: c.cpf });
      aplicaEnriquecimento(data || {});
    } catch (e) {
      /* segue manual */
    }
  }
});

/* CEP → ViaCEP no blur (mesmo comportamento da referência). */
const buscarCep = async () => {
  const c = onlyDigits(f.value.cep);
  if (c.length !== 8) return;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
    const d = await r.json();
    if (d.erro) return;
    f.value.logradouro = d.logradouro || f.value.logradouro;
    f.value.bairro = d.bairro || f.value.bairro;
    f.value.cidade = d.localidade || f.value.cidade;
    f.value.uf = (d.uf || f.value.uf).toUpperCase();
    useAlert('Endereço preenchido pelo CEP');
  } catch (e) {
    /* silencioso — usuário preenche na mão */
  }
};

/* ------------------------------------------------------------ validação -- */
const faltando = () => {
  const req = [
    ['nome', 'Nome completo'],
    ['nascimento', 'Nascimento'],
    ['celular', 'Celular'],
    ['cep', 'CEP'],
    ['logradouro', 'Rua'],
    ['numero', 'Número'],
    ['bairro', 'Bairro'],
    ['cidade', 'Cidade'],
    ['uf', 'UF'],
  ];
  if (mostraNomeMae.value) req.push(['nome_mae', 'Nome da mãe']);
  if (mostraRgRenda.value) req.push(['rg_numero', 'RG'], ['renda', 'Renda']);
  const faltas = req.filter(([k]) => !String(f.value[k] || '').trim()).map(([, r]) => r);
  if (f.value.recebTipo === 'pix' && temPix.value) {
    if (!String(f.value.pix_chave || '').trim()) faltas.push('Chave PIX');
  } else if (temConta.value) {
    if (!onlyDigits(f.value.banco_codigo)) faltas.push('Banco (nº)');
    if (!onlyDigits(f.value.agencia)) faltas.push('Agência');
    if (!onlyDigits(f.value.conta)) faltas.push('Conta');
  }
  return faltas;
};

/* -------------------------------------------------------------- submit -- */
const montarPayload = () => ({
  oferta: props.oferta || {},
  form: {
    nome: f.value.nome.trim(),
    cpf: onlyDigits(props.cliente?.cpf),
    nascimento: f.value.nascimento,
    celular: onlyDigits(f.value.celular),
    email: f.value.email.trim(),
    nome_mae: f.value.nome_mae.trim(),
    rg_numero: f.value.rg_numero.trim(),
    rg_orgao: f.value.rg_orgao.trim(),
    rg_uf: f.value.rg_uf.trim().toUpperCase(),
    rg_data: f.value.rg_data.trim(),
    sexo: f.value.sexo,
    estado_civil: f.value.estado_civil,
    renda: f.value.renda,
    ocupacao: f.value.ocupacao.trim(),
    matricula: f.value.matricula.trim(),
    endereco: {
      cep: onlyDigits(f.value.cep),
      logradouro: f.value.logradouro.trim(),
      numero: f.value.numero.trim(),
      complemento: f.value.complemento.trim(),
      bairro: f.value.bairro.trim(),
      cidade: f.value.cidade.trim(),
      uf: f.value.uf.trim().toUpperCase(),
    },
    recebimento: {
      tipo: temPix.value && f.value.recebTipo === 'pix' ? 'pix' : 'conta',
      pix_tipo: f.value.pix_tipo,
      pix_chave: f.value.pix_chave.trim(),
      banco_codigo: onlyDigits(f.value.banco_codigo),
      agencia: onlyDigits(f.value.agencia),
      agencia_digito: f.value.agencia_digito.trim(),
      conta: onlyDigits(f.value.conta),
      conta_digito: f.value.conta_digito.trim(),
      tipo_conta: f.value.tipo_conta,
    },
  },
});

const aplicarResposta = data => {
  const st = data?.status || 'erro';
  if (st === 'ok') {
    resultado.value = data;
    estado.value = 'ok';
    emit('ok', data);
  } else if (st === 'pendente') {
    estado.value = 'pendente';
    pollTimer = setTimeout(consultarStatus, 5000);
  } else {
    erroMsg.value = data?.mensagem || 'O banco recusou a digitação.';
    estado.value = 'form';
  }
};

const consultarStatus = async () => {
  try {
    const { data } = await simuladorApi.get(
      `simulacoes/${props.simulacaoId}/bancos/${b.value}/digitar/status`
    );
    aplicarResposta(data);
  } catch (e) {
    // um poll perdido não derruba: tenta de novo em 5s
    pollTimer = setTimeout(consultarStatus, 5000);
  }
};

const digitar = async () => {
  const faltas = faltando();
  if (faltas.length) {
    erroMsg.value = `Preencha: ${faltas.join(', ')}.`;
    return;
  }
  erroMsg.value = '';
  estado.value = 'enviando';
  try {
    const { data } = await simuladorApi.post(
      `simulacoes/${props.simulacaoId}/bancos/${b.value}/digitar`,
      montarPayload()
    );
    aplicarResposta(data);
  } catch (e) {
    erroMsg.value =
      e.response?.data?.detail ||
      e.response?.data?.error ||
      'Falha ao enviar a digitação.';
    estado.value = 'form';
  }
};

const copiarTexto = async (texto, aviso) => {
  if (!texto) return;
  try {
    await navigator.clipboard.writeText(texto);
  } catch (e) {
    const ta = document.createElement('textarea');
    ta.value = texto;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  useAlert(aviso);
};

const copiarLink = () =>
  copiarTexto(resultado.value?.link || '', 'Link copiado — envie ao cliente');
const copiarCodigo = () =>
  copiarTexto(resultado.value?.codigo || '', 'Código copiado');

/* v47: caso "em análise" da UY3 — proposta ok mas o SignUrl ainda não saiu.
   O botão re-consulta o banco pelo simulador-api. */
const verificandoLink = ref(false);
const verificarLink = async () => {
  const id = resultado.value?.propostaId;
  if (!id || verificandoLink.value) return;
  verificandoLink.value = true;
  try {
    const { data } = await simuladorApi.post(`propostas/${id}/atualizar-link`);
    if (data?.link) {
      resultado.value = { ...resultado.value, link: data.link };
      useAlert('Link liberado!');
    } else {
      useAlert(data?.mensagem || 'O banco ainda não liberou o link.');
    }
  } catch (e) {
    useAlert(e.response?.data?.detail || 'Falha ao consultar o link.');
  } finally {
    verificandoLink.value = false;
  }
};

const seguroTxt = computed(() => {
  const s = props.oferta?.detalhes?.seguro;
  if (s === true) return 'com seguro';
  if (s === false) return 'sem seguro';
  return '';
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-modal-backdrop-dark"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-3xl my-6 overflow-hidden border rounded-xl border-n-weak bg-n-background"
    >
      <!-- Cabeçalho -->
      <div
        class="flex items-center justify-between gap-3 px-5 py-4"
        :style="{ backgroundColor: estilo.bg, color: estilo.fg }"
      >
        <div class="flex flex-col min-w-0 gap-0.5">
          <span class="text-base font-bold truncate">
            Digitação · {{ estilo.label }} · {{ cliente.nome || cpfFmt(cliente.cpf) }}
          </span>
          <span class="text-xs opacity-90 tabular-nums">
            {{ brl(oferta.valor_liberado) }} · {{ oferta.prazo }}x de
            {{ brl(oferta.parcela) }}
            <template v-if="seguroTxt"> · {{ seguroTxt }}</template>
          </span>
        </div>
        <button type="button" class="sd-pill shrink-0" @click="emit('close')">
          <span class="i-lucide-x size-3" />
          Fechar
        </button>
      </div>

      <!-- ======================= ENVIANDO ======================= -->
      <div
        v-if="estado === 'enviando'"
        class="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center"
      >
        <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
        <p class="text-sm font-medium text-n-slate-12">
          Enviando a proposta ao {{ estilo.label }}…
        </p>
      </div>

      <!-- ======================= PENDENTE ======================= -->
      <div
        v-else-if="estado === 'pendente'"
        class="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center"
      >
        <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
        <p class="text-sm font-medium text-n-slate-12">
          O banco está processando a proposta…
        </p>
        <p class="text-xs text-n-slate-11">
          Acompanhando automaticamente — pode levar alguns minutos.
        </p>
      </div>

      <!-- ======================= RESULTADO ====================== -->
      <div v-else-if="estado === 'ok'" class="flex flex-col gap-4 px-6 py-8">
        <div class="flex items-center gap-2">
          <span class="i-lucide-check-circle-2 size-6" style="color: #16a34a" />
          <h3 class="text-lg font-bold text-n-slate-12">Proposta digitada!</h3>
        </div>
        <!-- ID da proposta: clicar copia o código completo -->
        <button
          v-if="resultado?.codigo"
          type="button"
          class="sd-sim-id text-left w-full cursor-pointer hover:bg-n-alpha-2 transition-colors"
          title="Clique para copiar o código"
          @click="copiarCodigo"
        >
          ID da proposta:
          <strong class="break-all">{{ resultado.codigo }}</strong>
          <span class="i-lucide-copy size-3 ml-1 inline-block align-middle" />
        </button>
        <p v-if="resultado?.mensagem" class="text-sm text-n-slate-11">
          {{ resultado.mensagem }}
        </p>
        <div v-if="resultado?.link" class="flex flex-wrap items-center gap-2">
          <button type="button" class="sd-btn-verde" @click="copiarLink">
            <span class="i-lucide-link size-4" />
            Copiar Link
          </button>
        </div>
        <!-- Sem link ainda: UY3 "em análise" ou V8 com timeout do
             formalization_url — badge âmbar + verificação no banco -->
        <div
          v-else-if="b === 'uy3' || b === 'v8'"
          class="flex flex-wrap items-center gap-2"
        >
          <span class="sd-badge-ambar">
            <span class="i-lucide-clock size-3.5" />
            Aguardando {{ estilo.label }}
          </span>
          <button
            type="button"
            class="sd-btn-ghost"
            :disabled="verificandoLink"
            @click="verificarLink"
          >
            <span
              :class="verificandoLink ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-rotate-cw'"
              class="size-4"
            />
            Verificar agora
          </button>
          <span class="w-full text-xs text-n-slate-11">
            A proposta está em análise no banco — o link de assinatura aparece
            aqui e na tela Propostas assim que ele for liberado.
          </span>
        </div>
        <p class="text-xs text-n-slate-11">
          A proposta foi registrada na tela <strong>Propostas</strong>.
        </p>
        <div>
          <button type="button" class="sd-btn-primary" @click="emit('close')">
            Fechar
          </button>
        </div>
      </div>

      <!-- ======================= FORMULÁRIO ===================== -->
      <div v-else class="flex flex-col gap-4 px-6 py-5">
        <p
          v-if="erroMsg"
          class="flex items-start gap-2 p-3 text-sm border rounded-lg border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11"
        >
          <span class="i-lucide-alert-triangle size-4 shrink-0 mt-0.5" />
          <span class="break-words">{{ erroMsg }}</span>
        </p>

        <!-- Dados pessoais -->
        <h4 class="sd-dig-secao">Dados pessoais</h4>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="sd-label">
            Nome completo
            <input v-model="f.nome" class="sd-input mt-1" />
          </label>
          <label class="sd-label">
            Nascimento
            <input v-model="f.nascimento" type="date" class="sd-input mt-1 tabular-nums" />
          </label>
          <label class="sd-label">
            E-mail
            <input v-model="f.email" type="email" class="sd-input mt-1" />
          </label>
          <label class="sd-label">
            Celular (DDD + número)
            <input v-model="f.celular" class="sd-input mt-1 tabular-nums" />
          </label>
          <label v-if="mostraSexo" class="sd-label">
            Sexo
            <select v-model="f.sexo" class="sd-input mt-1">
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
            </select>
          </label>
          <label v-if="mostraEstadoCivil" class="sd-label">
            Estado civil
            <select v-model="f.estado_civil" class="sd-input mt-1">
              <option v-for="[v, r] in opcoesEstadoCivil" :key="v" :value="v">
                {{ r }}
              </option>
            </select>
          </label>
          <label v-if="mostraNomeMae" class="sd-label">
            Nome da mãe
            <input v-model="f.nome_mae" class="sd-input mt-1" />
          </label>
          <label v-if="mostraOcupacao" class="sd-label">
            Ocupação
            <input v-model="f.ocupacao" class="sd-input mt-1" />
          </label>
          <template v-if="mostraRgRenda">
            <label class="sd-label">
              RG
              <input v-model="f.rg_numero" class="sd-input mt-1 tabular-nums" />
            </label>
            <label class="sd-label">
              Órgão emissor
              <input v-model="f.rg_orgao" class="sd-input mt-1" />
            </label>
            <label class="sd-label">
              UF do RG
              <input v-model="f.rg_uf" maxlength="2" class="sd-input mt-1 uppercase" />
            </label>
            <label class="sd-label">
              Data de emissão
              <input v-model="f.rg_data" placeholder="aaaa-mm-dd" class="sd-input mt-1 tabular-nums" />
            </label>
            <label class="sd-label">
              Renda (R$)
              <input v-model="f.renda" class="sd-input mt-1 tabular-nums" />
            </label>
            <label class="sd-label">
              Matrícula
              <input v-model="f.matricula" class="sd-input mt-1 tabular-nums" />
            </label>
          </template>
        </div>

        <!-- Endereço -->
        <h4 class="sd-dig-secao">Endereço</h4>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label class="sd-label">
            CEP
            <input
              v-model="f.cep"
              class="sd-input mt-1 tabular-nums"
              @blur="buscarCep"
            />
          </label>
          <label class="sd-label sm:col-span-2">
            Rua
            <input v-model="f.logradouro" class="sd-input mt-1" />
          </label>
          <label class="sd-label">
            Número
            <input v-model="f.numero" class="sd-input mt-1 tabular-nums" />
          </label>
          <label class="sd-label">
            Complemento
            <input v-model="f.complemento" placeholder="Apto / bloco (opcional)" class="sd-input mt-1" />
          </label>
          <label class="sd-label">
            Bairro
            <input v-model="f.bairro" class="sd-input mt-1" />
          </label>
          <label class="sd-label sm:col-span-2">
            Cidade
            <input v-model="f.cidade" class="sd-input mt-1" />
          </label>
          <label class="sd-label">
            UF
            <input v-model="f.uf" maxlength="2" class="sd-input mt-1 uppercase" />
          </label>
        </div>

        <!-- Recebimento -->
        <h4 class="sd-dig-secao">
          Recebimento
          <span
            v-if="mostraToggleReceb"
            class="ml-1 text-[11px] font-normal normal-case text-n-slate-11"
          >
            (chave PIX ou conta bancária)
          </span>
        </h4>
        <div v-if="mostraToggleReceb" class="sd-sim-toggle w-fit">
          <button
            type="button"
            :class="f.recebTipo === 'pix' ? 'is-on' : ''"
            @click="f.recebTipo = 'pix'"
          >
            PIX
          </button>
          <button
            type="button"
            :class="f.recebTipo === 'conta' ? 'is-on' : ''"
            @click="f.recebTipo = 'conta'"
          >
            Conta bancária
          </button>
        </div>

        <div
          v-if="temPix && f.recebTipo === 'pix'"
          class="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <label class="sd-label">
            Tipo de chave
            <select v-model="f.pix_tipo" class="sd-input mt-1">
              <option value="cpf">CPF</option>
              <option value="telefone">Telefone</option>
              <option value="email">E-mail</option>
              <option value="aleatoria">Aleatória</option>
            </select>
          </label>
          <label class="sd-label">
            Chave PIX
            <input v-model="f.pix_chave" class="sd-input mt-1 tabular-nums" />
          </label>
        </div>

        <div
          v-if="temConta && (f.recebTipo === 'conta' || !temPix)"
          class="grid grid-cols-2 gap-3 sm:grid-cols-3"
        >
          <label class="sd-label">
            Tipo de conta
            <select v-model="f.tipo_conta" class="sd-input mt-1">
              <option value="corrente">Conta Corrente</option>
              <option value="poupanca">Poupança</option>
              <option value="pagamento">Conta Pagamento</option>
            </select>
          </label>
          <label class="sd-label">
            Banco (nº)
            <input v-model="f.banco_codigo" placeholder="033" class="sd-input mt-1 tabular-nums" />
          </label>
          <label class="sd-label">
            Agência
            <input v-model="f.agencia" class="sd-input mt-1 tabular-nums" />
          </label>
          <label class="sd-label">
            Dígito agência
            <input v-model="f.agencia_digito" class="sd-input mt-1 tabular-nums" />
          </label>
          <label class="sd-label">
            Conta
            <input v-model="f.conta" class="sd-input mt-1 tabular-nums" />
          </label>
          <label class="sd-label">
            Dígito conta
            <input v-model="f.conta_digito" class="sd-input mt-1 tabular-nums" />
          </label>
        </div>

        <!-- Ações -->
        <div class="flex items-center gap-2 pt-3 mt-1 border-t border-n-weak">
          <button type="button" class="sd-btn-digitar" @click="digitar">
            <span class="i-lucide-pen-line size-4" />
            Digitar proposta
          </button>
          <button type="button" class="sd-btn-ghost" @click="emit('close')">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
